import React, { useState, useEffect, useRef } from "react";
import { refreshToken } from "./utils/auth";
import { useNavigate } from "react-router-dom";
import "./styles/Payment.css";
import { Plus, User } from "lucide-react";

import ProfileCard from "./components/Profile";
import TopUpModal from "./components/TopUpModal";
import CheckoutWallet from "./components/CheckoutWallet";
import PhoneNumberInput from "./components/PhoneNumberInput";

import AFCBournemouthLogo from "./assets/Premier-league-logos/Bournemouth-logo.png";
import ArsenalLogo from "./assets/Premier-league-logos/Arsenal-logo.png";
import AstonVillaLogo from "./assets/Premier-league-logos/Aston-villa-logo.png";
import BrentfordLogo from "./assets/Premier-league-logos/Brentford-logo.png";
import BrightonLogo from "./assets/Premier-league-logos/Brighton-logo.png";
import ChelseaLogo from "./assets/Premier-league-logos/Chelsea-logo.png";
import CoventryLogo from "./assets/Premier-league-logos/Coventry.png";
import CrystalPalaceLogo from "./assets/Premier-league-logos/Crystal-palace-logo.png";
import EvertonLogo from "./assets/Premier-league-logos/Everton-logo.png";
import FulhamLogo from "./assets/Premier-league-logos/Fulham-logo.png";
import HullLogo from "./assets/Premier-league-logos/Hull-city-logo.png";
import IpswichLogo from "./assets/Premier-league-logos/Ipswich-logo.png";
import LeedsLogo from "./assets/Premier-league-logos/Leeds-United-logo.png";
import LiverpoolLogo from "./assets/Premier-league-logos/Liverpool-logo.png";
import ManchesterCityLogo from "./assets/Premier-league-logos/Manchester-city-logo.png";
import ManchesterUnitedLogo from "./assets/Premier-league-logos/Manchester-United-logo.png";
import NewcastleLogo from "./assets/Premier-league-logos/Newcastle-logo.png";
import NottinghamForestLogo from "./assets/Premier-league-logos/Nottingham-forest-logo.png";
import SunderlandLogo from "./assets/Premier-league-logos/Sunderland-logo.png";
import TottenhamLogo from "./assets/Premier-league-logos/Spurs-logo.png";

const logos = [
  AFCBournemouthLogo,
  ArsenalLogo,
  AstonVillaLogo,
  BrentfordLogo,
  BrightonLogo,
  ChelseaLogo,
  CoventryLogo,
  CrystalPalaceLogo,
  EvertonLogo,
  FulhamLogo,
  HullLogo,
  IpswichLogo,
  LeedsLogo,
  LiverpoolLogo,
  ManchesterCityLogo,
  ManchesterUnitedLogo,
  NewcastleLogo,
  NottinghamForestLogo,
  TottenhamLogo,
  SunderlandLogo,
];

function Payment() {
  const navigate = useNavigate();
  const greetingRef = useRef(null);

  const [username, setUsername] = useState("");
  const [isGuest, setIsGuest] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showCheckoutWallet, setShowCheckoutWallet] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [autoPaymentProcessing, setAutoPaymentProcessing] = useState(false);
  const [selectedAutoAmount, setSelectedAutoAmount] = useState(null);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const scrollToGreeting = () => {
    greetingRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem("username");
    const guestStatus = localStorage.getItem("isGuest");
    if (storedUsername) {
      setUsername(storedUsername);
      setIsGuest(false);
    } else if (guestStatus === "true") {
      setUsername("Guest");
      setIsGuest(true);
    } else {
      setUsername("");
      setIsGuest(false);
    }
  }, []);

  const fetchWalletBalance = async () => {
    try {
      const token = localStorage.getItem("token");
      const BACKEND_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000";
      let res = await fetch(`${BACKEND_URL}/api/user`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
      });

      if (res.status === 401) {
        const newToken = await refreshToken();

        if (newToken) {
          res = await fetch(`${BACKEND_URL}/api/user`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${newToken}`,
            },
            credentials: "include",
          });
        }
      }
      const data = await res.json();
      if (data.wallet !== undefined) {
        setWalletBalance(data.wallet);
      }
    } catch (error) {
      console.error("Failed to fetch wallet balance:", error);
    }
  };

  useEffect(() => {
    fetchWalletBalance();
  }, []);

  const getGameweek = async () => {
    try {
      const BACKEND_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await fetch(`${BACKEND_URL}/current-gameweek`);
      if (!res.ok) {
        console.error(
          "Failed to fetch current gameweek from backend",
          res.status,
        );
        return null;
      }
      const data = await res.json();
      return data.currentGameweek ?? null;
    } catch (err) {
      console.error("Error getting backend gameweek:", err);
      return null;
    }
  };

  const handleAutoPaymentClick = (amount) => {
    setSelectedAutoAmount(amount);
    setShowPhoneInput(true); // open phone modal
  };

  useEffect(() => {
    setGlobalLoading(autoPaymentProcessing || isJoining);
  }, [autoPaymentProcessing, isJoining]);

  useEffect(() => {
    const checkDeadline = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/gameweek/status`);
        const data = await res.json();
        setIsOpen(Boolean(data.isOpen));
      } catch (err) {
        console.error("Failed to check deadline:", err);
      }
    };
    checkDeadline();
  }, [BACKEND_URL]);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Polls the backend every 3s for up to ~90s
  const waitForPayment = async (checkoutId) => {
    for (let i = 0; i < 30; i++) {
      await sleep(3000);
      try {
        const res = await fetch(`${BACKEND_URL}/api/pay/status/${checkoutId}`);
        const data = await res.json();
        if (data.status === "SUCCESS") return "SUCCESS";
        if (data.status === "FAILED") return "FAILED";
      } catch (err) {
        console.warn("Status check failed, retrying:", err);
      }
    }
    return "TIMEOUT";
  };

  const handleSTKPush = async (phone, amount) => {
    const token = localStorage.getItem("token");
    const fplId = localStorage.getItem("fplId");
    const userId = localStorage.getItem("userId");

    try {
      setAutoPaymentProcessing(true);

      const currentGameweek = await getGameweek();
      if (!currentGameweek) {
        alert("Could not determine current gameweek. Try again.");
        return;
      }

      // 1. Initiate STK push
      const initRes = await fetch(`${BACKEND_URL}/api/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          phone,
          amount,
          fplId,
          userId,
          entryAmount: amount,
          gameweek: currentGameweek,
        }),
      });
      const initData = await initRes.json();
      if (!initData.success || !initData.checkoutId) {
        alert(initData.message || "Payment initiation failed");
        return;
      }

      setShowPhoneInput(false);
      alert("Check your phone to complete the payment");

      // 2. Wait for the Daraja callback to confirm payment
      const result = await waitForPayment(initData.checkoutId);

      if (result === "FAILED") {
        alert("Payment was cancelled or failed. You have not been charged.");
        return;
      }
      if (result === "TIMEOUT") {
        alert(
          "We haven't received payment confirmation yet. If money was deducted, please contact support.",
        );
        return;
      }

      // 3. Payment succeeded: join the league using the checkoutId
      const joinBody = {
        fplId,
        entryAmount: amount,
        gameweek: currentGameweek,
        checkoutId: initData.checkoutId,
      };

      let joinRes = await fetch(`${BACKEND_URL}/api/join-league`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(joinBody),
      });

      if (joinRes.status === 401) {
        const newToken = await refreshToken();
        if (newToken) {
          joinRes = await fetch(`${BACKEND_URL}/api/join-league`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${newToken}`,
            },
            credentials: "include",
            body: JSON.stringify(joinBody),
          });
        }
      }

      if (joinRes.status === 201) {
        localStorage.setItem("entryAmount", amount);
        localStorage.setItem("joinedLeague", "true");
        localStorage.setItem("gameweek", currentGameweek);
        navigate("/leagues");
      } else {
        const errBody = await joinRes.json().catch(() => ({}));
        alert(
          errBody.message ||
            "Payment received but joining failed. Please contact support.",
        );
      }
    } catch (err) {
      console.error("STK push error:", err);
      alert("Error processing payment");
    } finally {
      setAutoPaymentProcessing(false);
    }
  };
  return (
    <div className="payment-page">
      <div className="nav-bar">
        {logos.map((logo, i) => (
          <img key={i} src={logo} alt={`Team ${i + 1}`} className="team-logo" />
        ))}
      </div>

      <div className="hero">
        <div className="background-blur" />
        <div className="hero-overlay">
          <h1>Ready for the Gameweek?</h1>
          <div className="hero-buttons">
            <div className="join-league-and-gameweek-buttons">
              <button onClick={scrollToGreeting}>Join weekly League</button>
              <button onClick={() => navigate("/season-league")}>
                Join Season league
              </button>
            </div>
            <div className="head-to-head-div">
              <button onClick={() => navigate("/head-to-head")}>
                Head To Head
              </button>
            </div>
          </div>
        </div>
        <div className="scroll-down-text">↓ Scroll down</div>
      </div>

      <div className="greetings" ref={greetingRef}>
        <h2>Hello, {username}</h2>
      </div>

      <div className="payment-body">
        <div className="rules">
          <h2>How to play</h2>
          <ul>
            <li>The game relies on official FPL points.</li>
            <li>
              You’ll be placed in a random 10-player league based on
              registration time.
            </li>
            <li>The top player in each league wins the prize.</li>
            <li>
              In case of a tie, bench points will be considered. Shared win if
              still tied.
            </li>
            <li>
              Entry fee is <strong>Min:KSh 50,or desired amount</strong>, paid
              via M-Pesa.
            </li>
            <li>Leagues reset every week.</li>
            <li>
              Each extra transfer beyond the free ones in FPL will reduce your
              total gameweek points.
            </li>
            <li>
              Chips are not allowed. If you know you will be using a chip that
              week <strong>Don't</strong> join that week's league
            </li>
          </ul>
          <p>
            <strong>Good Luck</strong>
          </p>
        </div>

        {!isGuest && (
          <div className="payment-checkout">
            <div className="wallet-checkout">
              <div className="wallet-header">
                <h3>Wallet</h3>
                <User
                  className="profile-icon"
                  size={24}
                  strokeWidth={2}
                  onClick={() => setShowProfile(true)}
                />
              </div>
              <div className="wallet-balance">
                <span>Balance:</span>
                <strong>KSh&nbsp;{walletBalance}</strong>
              </div>

              <div className="top-up">
                <button
                  onClick={() => setShowTopUp(true)}
                  className="top-up-button"
                >
                  Top up
                  <Plus size={16} strokeWidth={2} />
                </button>

                <button
                  className="wallet-pay-button"
                  onClick={() => setShowCheckoutWallet(true)}
                >
                  Join League from Wallet
                </button>

                <p className="wallet-note">
                  Click Join League from wallet after successful top-up
                </p>

                {showTopUp && (
                  <TopUpModal
                    onClose={() => setShowTopUp(false)}
                    onSuccess={(newBalance) => {
                      setWalletBalance(newBalance);
                      setShowTopUp(false);
                      fetchWalletBalance();
                    }}
                  />
                )}
              </div>
            </div>

            <div className="autoPayment-x-viewLeague">
              <div className="auto-checkout">
                <h3>Auto Checkout</h3>
                <p>Select an amount to pay directly with M-Pesa:</p>
                <div className="auto-buttons">
                  {[50, 100, 250].map((ksh) => (
                    <button
                      key={ksh}
                      className="auto-btn"
                      onClick={() => handleAutoPaymentClick(ksh)}
                      disabled={!isOpen || autoPaymentProcessing}
                    >
                      {autoPaymentProcessing && selectedAutoAmount === ksh
                        ? `Processing KSh ${ksh}...`
                        : `KSh\u00A0${ksh}`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="view-leagues">
                <button onClick={() => navigate("/leagues")}>
                  View Leagues
                </button>
              </div>
            </div>
          </div>
        )}

        {showCheckoutWallet && (
          <CheckoutWallet
            onClose={() => setShowCheckoutWallet(false)}
            onSelectAmount={async (amount) => {
              if (isJoining) return;
              setIsJoining(true);

              try {
                if (walletBalance < amount) {
                  alert(
                    `Insufficient wallet balance for KSh ${amount}. Please top up.`,
                  );
                  setShowCheckoutWallet(false);
                  setShowTopUp(true);
                  setIsJoining(false);
                  return;
                }

                const currentGameweek = await getGameweek();
                if (!currentGameweek) {
                  alert(
                    "Could not determine current gameweek. Try again shortly.",
                  );
                  setIsJoining(false);
                  return;
                }

                const BACKEND_URL =
                  process.env.REACT_APP_API_URL || "http://localhost:5000";
                const token = localStorage.getItem("token");

                let joinRes = await fetch(`${BACKEND_URL}/api/join-league`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  },
                  credentials: "include",
                  body: JSON.stringify({
                    fplId: localStorage.getItem("fplId"),
                    entryAmount: amount,
                    gameweek: currentGameweek,
                  }),
                });

                if (joinRes.status === 401) {
                  const newToken = await refreshToken();

                  if (newToken) {
                    joinRes = await fetch(`${BACKEND_URL}/api/join-league`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${newToken}`,
                      },
                      credentials: "include",
                      body: JSON.stringify({
                        fplId: localStorage.getItem("fplId"),
                        entryAmount: amount,
                        gameweek: currentGameweek,
                      }),
                    });
                  }
                }

                if (joinRes.status === 201) {
                  try {
                    let walletRes = await fetch(`${BACKEND_URL}/api/user`, {
                      method: "GET",
                      headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      credentials: "include",
                    });

                    if (walletRes.status === 401) {
                      const newToken = await refreshToken();

                      if (newToken) {
                        walletRes = await fetch(`${BACKEND_URL}/api/user`, {
                          method: "GET",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${newToken}`,
                          },
                          credentials: "include",
                        });
                      }
                    }
                    const walletData = await walletRes.json();
                    if (walletData.wallet !== undefined)
                      setWalletBalance(walletData.wallet);
                  } catch (e) {
                    console.warn("Failed to refresh wallet after join:", e);
                  }

                  localStorage.setItem("entryAmount", amount);
                  localStorage.setItem("joinedLeague", "true");
                  localStorage.setItem("gameweek", currentGameweek);
                  setShowCheckoutWallet(false);
                  navigate("/leagues");
                } else if (joinRes.status === 409) {
                  const errBody = await joinRes.json().catch(() => ({}));
                  alert(errBody.message || "You already joined this gameweek.");
                  setShowCheckoutWallet(false);
                } else {
                  const errBody = await joinRes.json().catch(() => ({}));
                  alert(errBody.message || "Failed to join league. Try again.");
                }
              } catch (err) {
                console.error("Error during wallet join flow:", err);
                alert("An error occurred. Please try again.");
              } finally {
                setIsJoining(false);
              }
            }}
          />
        )}

        {showProfile && (
          <ProfileCard
            username={username}
            walletBalance={walletBalance}
            onWithdrawSuccess={(newWallet) => setWalletBalance(newWallet)}
            onTopUp={() => setShowTopUp(true)}
            onClose={() => setShowProfile(false)}
          />
        )}

        {isGuest && (
          <div className="guest-redirect">
            <h3>Want to Join a League?</h3>
            <p>Log in to access full features and join paid leagues.</p>
            <button
              className="login-redirect-button"
              onClick={() => navigate("/")}
            >
              Log in to Join League
            </button>
          </div>
        )}
      </div>

      {/* Phone Number Input Modal */}
      {showPhoneInput && (
        <PhoneNumberInput
          amount={selectedAutoAmount}
          onSubmit={(phone) => handleSTKPush(phone, selectedAutoAmount)}
          onCancel={() => setShowPhoneInput(false)}
        />
      )}

      {globalLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner"></div>
            <p>Processing your payment, please wait...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payment;
